import type { ReactNode } from 'react';
import { Tooltip } from '@mui/material';

export interface RwSegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Leading icon — rw-ds.css §.rw-segmented anatomy: "segment = label + optional leading icon". */
  icon?: ReactNode;
  disabled?: boolean;
  testId?: string;
}

export interface RwSegmentedProps<T extends string> {
  options: readonly RwSegmentedOption<T>[];
  value: T;
  onChange: (next: T) => void;
  /** DS `.rw-segmented` size step: m (40, default) or lg (48). */
  size?: 'm' | 'lg';
  /** Hug (default, content-driven) vs Fill (equal segments, stretches) — rw-ds-components §Content switcher. */
  fill?: boolean;
  /**
   * View-mode form: show each segment's icon ALONE, label carried as the accessible name + tooltip.
   * rw-ds-components §Content switcher: "view-mode (how the data renders — List/Grid/Map) = icons if
   * every segment has a standard one … Never mix" — so every option must supply an `icon`.
   */
  iconOnly?: boolean;
  'aria-label': string;
}

/**
 * rw-ds-components §Content switcher — single-select, always exactly one selected,
 * switching between views of the SAME content (never distinct sections — that's Tabs).
 * Renders the canonical `.rw-segmented` (raised white-chip selected fill on a tertiary
 * track, sharp corners, regular weight — design ruling 2026-07-27, supersedes the earlier
 * reverse-out treatment). Generalizes a 2-option project/rate style selector's markup to
 * N options (rw-ds.css's own anatomy comment: "track → 2+ segments").
 *
 * Sets a literal `data-testid` (via `option.testId`) rather than calling a `tid()` helper —
 * this repo has no `src/common/infrastructure/config/tid.ts` (see `.cursor/rules/test-id-naming-rules.mdc`).
 */
export function RwSegmented<T extends string>({
  options,
  value,
  onChange,
  size = 'm',
  fill = false,
  iconOnly = false,
  'aria-label': ariaLabel
}: RwSegmentedProps<T>) {
  const select = (next: T, disabled?: boolean) => {
    if (disabled || next === value) return;
    onChange(next);
  };

  const className = ['rw-segmented', fill ? 'rw-segmented--fill' : '', size === 'lg' ? 'rw-segmented--lg' : ''].filter(Boolean).join(' ');

  return (
    <div className={className} role="tablist" aria-label={ariaLabel}>
      {options.map((option) => {
        const segment = (
          <button
            key={option.value}
            type="button"
            role="tab"
            className="rw-segmented__segment"
            aria-selected={option.value === value}
            aria-label={iconOnly ? option.label : undefined}
            disabled={option.disabled}
            onClick={() => select(option.value, option.disabled)}
            {...(option.testId ? { 'data-testid': option.testId } : {})}
          >
            {option.icon ? <span className="rw-segmented__icon">{option.icon}</span> : null}
            {iconOnly ? null : option.label}
          </button>
        );
        // Icon-only carries its name in the tooltip as well as the aria-label, so the meaning is
        // available to sighted users too — an icon alone is not self-describing.
        return iconOnly ? (
          <Tooltip key={option.value} title={option.label} arrow placement="top">
            {segment}
          </Tooltip>
        ) : (
          segment
        );
      })}
    </div>
  );
}

export default RwSegmented;
